import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { LogOut, Plus, Pencil, Trash2, Loader2, Users, Activity, LayoutGrid, ArrowLeft, X, Menu, Contact, GripVertical } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ContentView from "@/components/dataroom/ContentView";
import CRM from "@/components/dataroom/CRM";
import { ThemeToggle, useThemeClass } from "@/components/ThemeToggle";
import { LOGO_URL } from "@/lib/nvsnContent";

const GROUP_ORDER = ["Overview", "Pillars", "Diligence"];
const inputCls = "w-full bg-[#141414] border border-[#2a2a2a] px-4 py-3 text-[13px] text-[#e8e8e8] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function PasscodeGate({ onSuccess }) {
  const navigate = useNavigate();
  const [pass, setPass] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { passcode: pass });
      onSuccess(data.token);
      toast.success("Admin access granted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect passcode");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#080808] text-[#e8e8e8] px-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-[12px] text-[#8f8f8f] hover:text-[#d4d4d4] mb-8 transition-colors"><ArrowLeft className="h-4 w-4" /> Back</button>
        <img src={LOGO_URL} alt="NVSN" className="h-[42px] w-auto" />
        <div className="mono-eyebrow text-[#bdbdbd] mt-6 mb-2">Restricted · Admin</div>
        <h1 className="font-serif-pf text-[36px] text-[#f0f0f0] mb-3">Admin console</h1>
        <p className="text-[#d4d4d4] text-[13px] mb-8 leading-[1.7]">Enter the admin passcode to manage sections, documents, and links, and to review investor access.</p>
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="admin-passcode" type="password" required value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Passcode" className={inputCls} />
          <button data-testid="admin-login-btn" disabled={loading} className="w-full bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] font-medium py-3.5 rounded transition-colors disabled:opacity-60">{loading ? "Verifying…" : "Unlock console"}</button>
        </form>
      </motion.div>
    </div>
  );
}

function SectionDialog({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ title: initial?.title || "", description: initial?.description || "", group: initial?.group || "Diligence", kind: initial?.kind || "content", sort_order: initial?.sort_order ?? 0 });
  const save = async (e) => {
    e.preventDefault();
    try {
      if (initial) await api.put(`/admin/sections/${initial.id}`, form);
      else await api.post("/admin/sections", form);
      toast.success(initial ? "Section updated" : "Section created");
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-6" onClick={onClose}>
      <div className="bg-[#0e0e0e] border border-[#222] rounded w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="section-dialog">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif-pf text-[24px] text-[#f0f0f0]">{initial ? "Edit section" : "New section"}</h3>
          <button onClick={onClose} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={save} className="space-y-4">
          <input required placeholder="Section title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} data-testid="section-title-input" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={inputCls} data-testid="section-desc-input" />
          <select value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} className={inputCls} data-testid="section-group-input">
            {GROUP_ORDER.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className={inputCls} data-testid="section-kind-input">
            <option value="content">Content (files &amp; links)</option>
            <option value="team">Team (member profiles)</option>
            <option value="captable">Cap table</option>
          </select>
          <input type="number" placeholder="Sort order" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} className={inputCls} />
          <button type="submit" className="w-full bg-[#4deb7a] text-[#080808] font-medium py-3 rounded" data-testid="save-section-btn">{initial ? "Save changes" : "Create section"}</button>
        </form>
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { isAdmin, loginAdmin, logoutAdmin } = useAuth();
  const [tab, setTab] = useState("content");
  const [sections, setSections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [activity, setActivity] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useThemeClass();
  const [dragSecId, setDragSecId] = useState(null);
  const [actInvestor, setActInvestor] = useState("all");
  const [actKind, setActKind] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sections");
      setSections(data);
      setActiveId((cur) => (data.find((s) => s.id === cur) ? cur : data[0]?.id || null));
    } finally { setLoading(false); }
  };
  const loadInsights = async () => {
    const [i, a] = await Promise.all([api.get("/admin/investors"), api.get("/admin/activity")]);
    setInvestors(i.data); setActivity(a.data);
  };
  useEffect(() => { if (isAdmin) { load(); loadInsights(); } }, [isAdmin]);

  if (!isAdmin) return <PasscodeGate onSuccess={loginAdmin} />;

  const active = sections.find((s) => s.id === activeId);
  const logout = () => { logoutAdmin(); navigate("/"); };
  const delSection = async (id) => {
    if (!window.confirm("Delete this section and its contents?")) return;
    await api.delete(`/admin/sections/${id}`); toast.success("Section deleted"); load();
  };
  const grouped = GROUP_ORDER.map((g) => ({ group: g, items: sections.filter((s) => s.group === g) })).filter((x) => x.items.length);
  const handleSectionDrop = async (targetId) => {
    if (!dragSecId || dragSecId === targetId) { setDragSecId(null); return; }
    const dragSec = sections.find((s) => s.id === dragSecId);
    const targetSec = sections.find((s) => s.id === targetId);
    if (!dragSec || !targetSec || dragSec.group !== targetSec.group) { setDragSecId(null); return; }
    const groupItems = sections.filter((s) => s.group === dragSec.group);
    const from = groupItems.findIndex((s) => s.id === dragSecId);
    const to = groupItems.findIndex((s) => s.id === targetId);
    const [moved] = groupItems.splice(from, 1);
    groupItems.splice(to, 0, moved);
    const rebuilt = GROUP_ORDER.flatMap((g) => (g === dragSec.group ? groupItems : sections.filter((s) => s.group === g)));
    const known = new Set(rebuilt.map((s) => s.id));
    const finalArr = [...rebuilt, ...sections.filter((s) => !known.has(s.id))];
    setSections(finalArr);
    setDragSecId(null);
    try {
      await api.put("/admin/sections/reorder", { order: finalArr.map((s) => s.id) });
    } catch { toast.error("Failed to save order"); load(); }
  };
  const goToSlug = (slug) => { const s = sections.find((x) => x.slug === slug); if (s) { setActiveId(s.id); setMenuOpen(false); } };
  const selectSection = (id) => { setActiveId(id); setMenuOpen(false); };
  const selectTab = (id) => { setTab(id); setMenuOpen(false); };
  const tabs = [
    { id: "content", label: "Content", icon: LayoutGrid },
    { id: "crm", label: "CRM", icon: Contact },
    { id: "investors", label: "Investors", icon: Users },
    { id: "activity", label: "Activity", icon: Activity },
  ];

  const NavContent = () => (
    <>
      <div className="mb-4">
        {tabs.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => selectTab(t.id)}
            className={`w-full flex items-center gap-2.5 pl-6 pr-4 py-2.5 text-[12px] border-l-2 transition-colors ${tab === t.id ? "text-[#4deb7a] border-[#4deb7a] bg-[rgba(77,235,122,0.08)] font-medium" : "text-[#d4d4d4] border-transparent hover:text-[#e8e8e8]"}`}>
            <t.icon className="h-4 w-4" strokeWidth={1.5} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "content" && (
        <div className="border-t border-[#1a1a1a] pt-4">
          <div className="flex items-center justify-between px-6 mb-2">
            <span className="font-mono-dm uppercase tracking-[0.12em] text-[9px] text-[#8f8f8f]">Sections</span>
            <button data-testid="new-section-btn" onClick={() => setDialog({ section: null })} className="text-[#4deb7a] hover:opacity-70"><Plus className="h-4 w-4" /></button>
          </div>
          {grouped.map((grp) => (
            <div key={grp.group} className="mb-3">
              <div className="font-mono-dm uppercase tracking-[0.1em] text-[8px] text-[#8f8f8f] px-6 mb-1">{grp.group}</div>
              {grp.items.map((s) => (
                <div key={s.id}
                  draggable
                  onDragStart={() => setDragSecId(s.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleSectionDrop(s.id)}
                  className={`group flex items-center justify-between gap-1.5 pl-2 pr-3 py-2 border-l-2 transition-opacity ${dragSecId === s.id ? "opacity-40" : ""} ${activeId === s.id ? "border-[#4deb7a] bg-[rgba(77,235,122,0.08)]" : "border-transparent hover:bg-[#141414]"}`}>
                  <GripVertical data-testid={`drag-section-${s.id}`} className="h-3.5 w-3.5 text-[#5a5a5a] cursor-grab shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                  <button data-testid={`admin-nav-${s.id}`} onClick={() => selectSection(s.id)} className={`text-[12px] text-left truncate flex-1 ${activeId === s.id ? "text-[#4deb7a] font-medium" : "text-[#d4d4d4]"}`}>{s.title}</button>
                  <div className="flex items-center gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                    <button data-testid={`edit-section-${s.id}`} onClick={() => setDialog({ section: s })} className="text-[#8f8f8f] hover:text-[#e8e8e8]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`delete-section-${s.id}`} onClick={() => delSection(s.id)} className="text-[#8f8f8f] hover:text-[#c83232]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div className="dr-scale min-h-screen flex flex-col bg-[#080808] text-[#e8e8e8]">
      <header className="h-[60px] border-b border-[#222] px-5 sm:px-10 flex items-center justify-between sticky top-0 bg-[#080808] z-40">
        <div className="flex items-center gap-3">
          <button data-testid="menu-toggle" onClick={() => setMenuOpen(true)} className="md:hidden text-[#d4d4d4] hover:text-[#f0f0f0] transition-colors" aria-label="Open menu">
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
          <div className="hidden md:flex items-center gap-5">
            <img src={LOGO_URL} alt="NVSN" className="h-9 w-auto" />
            <span className="w-px h-[18px] bg-[#2a2a2a]" />
            <span className="mono-eyebrow text-[#bdbdbd]">Admin Console</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="NVSN" className="md:hidden h-8 w-auto" />
          <button data-testid="admin-logout" onClick={logout} className="hidden md:flex items-center gap-1.5 text-[12px] text-[#d4d4d4] hover:text-[#e8e8e8] transition-colors"><LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Exit</button>
        </div>
      </header>

      <div className="flex flex-1">
        <nav className="w-[244px] min-w-[244px] border-r border-[#222] pt-6 sticky top-[60px] h-[calc(100vh-60px)] overflow-y-auto hidden md:flex flex-col">
          <NavContent />
          <div className="mt-auto px-5 py-5 border-t border-[#1a1a1a]">
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>
        </nav>

        {/* Mobile Drawer */}
        <AnimatePresence>
          {menuOpen && (
            <div className="md:hidden fixed inset-0 z-50">
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setMenuOpen(false)} className="absolute inset-0 bg-black/70" />
              <motion.nav
                initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.25 }}
                className="absolute left-0 top-0 h-full w-[82%] max-w-[300px] bg-[#0b0b0b] border-r border-[#222] flex flex-col"
                data-testid="mobile-drawer"
              >
                <div className="h-[60px] px-6 flex items-center justify-between border-b border-[#222] shrink-0">
                  <img src={LOGO_URL} alt="NVSN" className="h-8 w-auto" />
                  <button data-testid="menu-close" onClick={() => setMenuOpen(false)} className="text-[#d4d4d4] hover:text-[#f0f0f0]"><X className="h-5 w-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto py-6"><NavContent /></div>
                <div className="border-t border-[#222] p-4 shrink-0">
                  <div className="mb-3"><ThemeToggle theme={theme} setTheme={setTheme} testid="theme-toggle-mobile" /></div>
                  <button data-testid="admin-logout-mobile" onClick={logout} className="w-full flex items-center gap-2 text-[13px] text-[#d4d4d4] hover:text-[#f0f0f0] px-2 py-2.5 border border-[#222] rounded transition-colors">
                    <LogOut className="h-4 w-4" strokeWidth={1.5} /> Exit console
                  </button>
                </div>
              </motion.nav>
            </div>
          )}
        </AnimatePresence>

        <main className="flex-1 min-w-0 w-full px-5 sm:px-10 lg:px-14 py-8 sm:py-12">
          {tab === "content" && (
            loading ? (
              <div className="flex items-center gap-2 text-[#8f8f8f] py-20 text-[13px]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
            ) : active ? (
              <ContentView section={active} isAdmin={true} onChanged={load} onNavigateSlug={goToSlug} />
            ) : (
              <div className="py-24 text-center text-[#8f8f8f]">
                <div className="text-[28px] opacity-30 mb-3">◻</div>
                <p className="font-serif-pf text-[24px] text-[#f0f0f0]">No sections yet</p>
                <button onClick={() => setDialog({ section: null })} className="mt-6 bg-[#4deb7a] text-[#080808] text-[12px] font-medium px-5 py-2.5 rounded inline-flex items-center gap-2"><Plus className="h-4 w-4" /> New section</button>
              </div>
            )
          )}

          {tab === "crm" && <CRM />}

          {tab === "investors" && (
            <div data-testid="investors-tab">
              <div className="mono-eyebrow text-[#4deb7a] mb-2.5">Access Log</div>
              <h1 className="font-serif-pf text-[28px] text-[#f0f0f0] mb-8">Registered investors</h1>
              {investors.length === 0 ? <p className="text-[#8f8f8f] text-[13px]">No investors have registered yet.</p> : (
                <div className="border-t border-[#222]">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-4 font-mono-dm uppercase tracking-[0.1em] text-[9px] text-[#8f8f8f] px-4 py-2 border-b border-[#222]"><span>Name</span><span>Company</span><span>Registered</span></div>
                  {investors.map((u) => (
                    <div key={u.id} className="grid grid-cols-[1fr_1fr_auto] gap-4 items-center px-4 py-3.5 border-b border-[#141414] hover:bg-[#0e0e0e] transition-colors" data-testid={`investor-${u.id}`}>
                      <div className="min-w-0"><div className="text-[13px] text-[#e8e8e8] truncate">{u.first_name} {u.last_name}</div><div className="text-[11px] text-[#8f8f8f] truncate">{u.email}</div></div>
                      <span className="text-[12px] text-[#d4d4d4] truncate">{u.company}</span>
                      <span className="font-mono-dm text-[10px] text-[#8f8f8f]">{fmtDT(u.created_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "activity" && (
            <div data-testid="activity-tab">
              <div className="mono-eyebrow text-[#4deb7a] mb-2.5">Audit</div>
              <h1 className="font-serif-pf text-[28px] text-[#f0f0f0] mb-6">Investor activity</h1>
              {(() => {
                const KIND_META = {
                  view: { label: "Section view", cls: "bg-[rgba(40,100,220,0.12)] text-[#5b8cff] border border-[rgba(40,100,220,0.25)]" },
                  file_view: { label: "File open", cls: "bg-[rgba(220,140,40,0.12)] text-[#e0a244] border border-[rgba(220,140,40,0.25)]" },
                  link_view: { label: "Link open", cls: "bg-[rgba(140,60,200,0.14)] text-[#b07adc] border border-[rgba(140,60,200,0.28)]" },
                  signup: { label: "Signup", cls: "bg-[rgba(77,235,122,0.08)] text-[#4deb7a] border border-[rgba(77,235,122,0.2)]" },
                  login: { label: "Login", cls: "bg-[#1a1a1a] text-[#bdbdbd] border border-[#2a2a2a]" },
                };
                const meta = (k) => KIND_META[k] || { label: k, cls: "bg-[#1a1a1a] text-[#bdbdbd] border border-[#2a2a2a]" };
                const filtered = activity.filter((a) => (actInvestor === "all" || a.user_id === actInvestor) && (actKind === "all" || a.kind === actKind));
                const kindsPresent = Array.from(new Set(activity.map((a) => a.kind)));
                const selCls = "bg-[#141414] border border-[#2a2a2a] text-[12px] text-[#e8e8e8] rounded px-3 py-2 focus:outline-none focus:border-[#4deb7a]";
                return (
                  <>
                    <div className="flex flex-wrap gap-2.5 mb-6">
                      <select data-testid="activity-filter-investor" value={actInvestor} onChange={(e) => setActInvestor(e.target.value)} className={selCls}>
                        <option value="all">All investors</option>
                        {investors.map((u) => (
                          <option key={u.id} value={u.id}>{u.first_name} {u.last_name}{u.company ? ` · ${u.company}` : ""}</option>
                        ))}
                      </select>
                      <select data-testid="activity-filter-kind" value={actKind} onChange={(e) => setActKind(e.target.value)} className={selCls}>
                        <option value="all">All events</option>
                        {kindsPresent.map((k) => (
                          <option key={k} value={k}>{meta(k).label}</option>
                        ))}
                      </select>
                      <span className="ml-auto self-center font-mono-dm text-[10px] text-[#8f8f8f]">{filtered.length} event{filtered.length === 1 ? "" : "s"}</span>
                    </div>
                    {filtered.length === 0 ? <p className="text-[#8f8f8f] text-[13px]">No activity matches these filters.</p> : (
                      <div className="border-t border-[#222]">
                        {filtered.map((a) => (
                          <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[#141414] hover:bg-[#0e0e0e] transition-colors" data-testid={`activity-row-${a.id}`}>
                            <div className="min-w-0">
                              <div className="text-[13px] text-[#e8e8e8] truncate"><span>{a.user_name}</span> {a.company && <span className="text-[#8f8f8f]">· {a.company}</span>}</div>
                              <div className="text-[11px] text-[#d4d4d4] mt-0.5 truncate">{a.detail}</div>
                            </div>
                            <div className="text-right shrink-0 ml-4">
                              <span className={`font-mono-dm text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${meta(a.kind).cls}`}>{meta(a.kind).label}</span>
                              <div className="font-mono-dm text-[10px] text-[#8f8f8f] mt-1">{fmtDT(a.created_at)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </main>
      </div>

      {dialog && <SectionDialog initial={dialog.section} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}
